import { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Button from '../../../components/v2/buttons/Button'
import StartOver from '../../../components/v2/buttons/StartOver'
import PaymentCard from '../../../components/v2/cards/PaymentCard'
import BaseModal from '../../../components/v2/modals/BaseModal'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { clearInternationaTplStore, IntTplState } from '../../../store/internationalTplSlice'

const InternationalTplPayment = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isProduction } = useAppSelector((root: { app: AppState }) => root.app)

  const [startOverModal, setStartOverModal] = useState<boolean>(false)
  const { checkout } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)
  const [selectedPaymentMethodState, setSelectedPaymentMethodState] = useState<string | undefined>(undefined)

  // TODO ADD INTERNATIONAL TPL PAYMENT
  const [getPaymentUrl, { data: paymentUrl, isLoading: isPaymentLoading }] = api.useLazyGetTravelPolicyPaymentUrlQuery()
  const [afterPaymentStepsOnDev, { isLoading: isAfterPaymentStepsOnDevLoading }] =
    api.useAfterPaymentStepsOnDevMutation()

  // look order id and payment method
  const _getPaymentUrlCallback = useCallback(async () => {
    if (checkout.offer.orderId && selectedPaymentMethodState && isProduction) {
      try {
        await getPaymentUrl({ orderId: checkout.offer.orderId, paymentType: 'other' }).unwrap()
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.error(errorMessage)
        } else {
          toast.error(`${t('anErrorOccurred')} InternationaTplPayment.tsx`)
        }
      }
    }
  }, [checkout, getPaymentUrl, selectedPaymentMethodState, t, isProduction])

  // get payment url
  useEffect(() => {
    _getPaymentUrlCallback()
    return () => {}
  }, [_getPaymentUrlCallback])

  // main payment
  const mainPayment = () => {
    if (checkout.offer.orderId && isProduction) {
      // got to checkout page
      if (paymentUrl && paymentUrl.url && isProduction) {
        // clear abroad rtk store
        dispatch(clearInternationaTplStore())
        //
        document.location.href = paymentUrl.url
      }
    }
  }

  // fake payment
  const fakePayment = async () => {
    if (checkout?.offer.orderId && !isProduction) {
      try {
        await afterPaymentStepsOnDev({
          orderId: checkout?.offer?.orderId,
          amount: checkout?.offer.premium as number,
        }).unwrap()
        toast.success(`${t('paymentSuccessfully')}`)
        dispatch(clearInternationaTplStore())
        navigate('/pay/payment-success')
      } catch (error: any) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0]
          const errorMessage = firstError.message
          toast.info(errorMessage)
        } else {
          toast.error('შეცდომა გადახდის გვერდზე, internationalTPLfakePayment.tsx')
        }
      }
    }
  }

  // disable browser back button
  useEffect(() => {
    const disableBackButton = (e: any) => {
      e.preventDefault()
      window.history.forward()
    }

    window.history.pushState('', '', window.location.href)
    window.addEventListener('popstate', disableBackButton)

    return () => {
      window.removeEventListener('popstate', disableBackButton)
    }
  }, [])

  const MainLoading = isPaymentLoading || isAfterPaymentStepsOnDevLoading

  return MainLoading ? (
    <Loader text={t('redirectionToPayment')} />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{t('chooseCard')}</title>
        <meta name="description" content={t('chooseCard')} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <BaseModal isOpen={startOverModal} onClose={() => setStartOverModal(!startOverModal)}>
        <StartOverDiv>
          <h5>{t('areyouSureTostartOver')}</h5>
          <div>
            <Button
              fullWidth={true}
              text={t('yes')}
              buttonType="primary"
              onClick={() => [dispatch(clearInternationaTplStore()), navigate('/international-tpl-country')]}
            />
            <Button
              fullWidth={true}
              text={t('no')}
              buttonType="transparent"
              onClick={() => setStartOverModal(!startOverModal)}
            />
          </div>
        </StartOverDiv>
      </BaseModal>

      <ScreenHeader
        rightSideChildren={<StartOver text={t('startOver')} onClick={() => setStartOverModal(!startOverModal)} />}
      />

      <Content>
        <Title text={t('chooseCard')} />

        <Cards>
          {['master', 'visa'].map((item, index) => {
            return (
              <PaymentCard
                key={index}
                cardName={item === 'master' ? 'Mastercard' : 'Visa'}
                selected={item === selectedPaymentMethodState}
                cardType={item}
                onClick={() => {
                  if (selectedPaymentMethodState === item) {
                    setSelectedPaymentMethodState(undefined)
                  } else {
                    // always other way
                    setSelectedPaymentMethodState(item)
                  }
                }}
              />
            )
          })}

          {selectedPaymentMethodState && checkout && checkout.offer.orderId ? (
            <Button
              fullWidth={false}
              style={{
                marginTop: '48px',
              }}
              text={t('pay')}
              buttonType="primary"
              onClick={() => {
                // main payment
                if (checkout && checkout.offer.orderId && isProduction) {
                  mainPayment()
                }
                // fake payment
                if (checkout && checkout.offer.orderId && !isProduction) {
                  fakePayment()
                }
              }}
            />
          ) : null}
        </Cards>
      </Content>

      <BackgroundIcon />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  min-height: 100vh;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
`
const Content = styled.div`
  position: relative;
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;
  width: 100%;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`
const Cards = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-block: 32px;
`
const StartOverDiv = styled.div`
  h5 {
    text-align: center;
    color: ${Colors.primaryBlack};
  }

  div {
    margin-top: 50px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr;
    grid-column-gap: 20px;
    grid-row-gap: 0px;
  }

  ${Responsive.mobile} {
    div {
      grid-template-columns: repeat(2, 130px);
      justify-content: center;
    }
  }
`

export default InternationalTplPayment
